import {
  EmailRounded,
  PhoneRounded,
  RateReviewRounded,
  RemoveCircle,
  SmsRounded,
  TagOutlined,
} from "@mui/icons-material";
import { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import CRMMenu from "../../../App/Components/Menu/CRMMenu";
import { AppFeatureLookup } from "../../../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage } from "../../../App/Constants/CrmLiteConstants";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import {
  toSelectedCandidate,
  useMessageDialogHandlers,
} from "../../../App/State/Hooks/MessageDialog/useMessageDialog";
import { ICandidate } from "../../../App/Typings/Candidate";
import { IMessageContainers } from "../../../MessageComponents/Enums/MessageEnums";

export function CandidateActions({
  candidate,
  handleSelectCandidateToRemove,
  setSelectedCandidate,
  canRemoveCandidates,
  onEditTagsClick,
}: {
  candidate: ICandidate;
  handleSelectCandidateToRemove: (candidateId: string) => void;
  setSelectedCandidate: Dispatch<SetStateAction<ICandidate | undefined>>;
  canRemoveCandidates: boolean;
  onEditTagsClick: (candidate: ICandidate) => void;
}) {
  const navigate = useNavigate();
  const { handleOpenDialog } = useMessageDialogHandlers();

  const candidateEmail = candidate.emailAddresses.find(
    (email) => (email.validationAttempt && email.isValid) || !email.validationAttempt,
  );
  const hasEmail = candidateEmail !== undefined;
  const candidatePhone = candidate.phoneNumbers.find((phoneNumber) => phoneNumber.isValid);
  const hasPhone = candidatePhone !== undefined;

  const emailDirectMessaging = hasEmail
    ? "Email candidate directly."
    : "Candidate does not have an email address";
  const callDirectMessaging = hasPhone
    ? `Call ${candidate.firstName}`
    : "Candidate does not have a valid phone number";
  const textDirectMessaging = hasPhone
    ? "Send text message directly to candidate."
    : "Candidate does not have a valid phone number";

  const handleOpenMessageDialog = (actionCandidate: ICandidate, containerType: IMessageContainers) => {
    setSelectedCandidate(actionCandidate);
    handleOpenDialog({
      containerType,
      candidateIds: [actionCandidate.id],
      candidateEntities: {
        [actionCandidate.id]: toSelectedCandidate(actionCandidate),
      },
    });
  };

  // permissions
  const accessEmailCandidateAction = useFeaturePermission(AppFeatureLookup.GroupEmailCandidateAction);
  const accessCallCandidateAction = useFeaturePermission(AppFeatureLookup.GroupCallCandidateAction);
  const accessTextCandidateAction = useFeaturePermission(AppFeatureLookup.GroupTextCandidateAction);
  const accessRemoveCandidateAction = useFeaturePermission(AppFeatureLookup.GroupRemoveCandidateAction);

  const key = `group-candidate-quick-actions-${candidate.id}`;

  return (
    <CRMMenu
      key={key}
      id={key}
      title="Group candidate quick actions"
      links={[
        {
          dataTestId: `groupprofile-candidate-actions-note-${candidate.id}`,
          title: "Add a note",
          tooltip: `Add a note for ${candidate.firstName}`,
          icon: <RateReviewRounded sx={{ fontSize: "1rem" }} />,
          onClick: () => navigate(`/candidates/${candidate.id}`),
        },
        {
          dataTestId: `groupprofile-candidate-actions-email-${candidate.id}`,
          title: "Email Candidate",
          tooltip: accessEmailCandidateAction ? emailDirectMessaging : crmLiteUpgradeMessage,
          icon: <EmailRounded sx={{ fontSize: "1rem" }} />,
          onClick: accessEmailCandidateAction
            ? () => handleOpenMessageDialog(candidate, IMessageContainers.CandidateEmailNow)
            : undefined,
          disabled: !accessEmailCandidateAction || !hasEmail,
        },
        {
          dataTestId: `groupprofile-candidate-actions-call-${candidate.id}`,
          title: "Call Candidate",
          tooltip: accessCallCandidateAction ? callDirectMessaging : crmLiteUpgradeMessage,
          icon: <PhoneRounded sx={{ fontSize: "1rem" }} />,
          isLink: true,
          href: `tel:${candidatePhone?.phoneNumber ?? ""}`,
          disabled: !accessCallCandidateAction || !hasPhone,
        },
        {
          dataTestId: `groupprofile-candidate-actions-sms-${candidate.id}`,
          title: "Text Message Candidate",
          tooltip: accessTextCandidateAction ? textDirectMessaging : crmLiteUpgradeMessage,
          icon: <SmsRounded sx={{ fontSize: "1rem" }} />,
          disabled: !accessTextCandidateAction || !hasPhone,
          onClick: accessTextCandidateAction
            ? () => handleOpenMessageDialog(candidate, IMessageContainers.CandidateTextNow)
            : undefined,
        },
        {
          dataTestId: `groupprofile-candidate-actions-tags-${candidate.id}`,
          title: "Edit candidate tags",
          tooltip: "Edit candidate tags",
          icon: <TagOutlined sx={{ fontSize: "1rem" }} />,
          onClick: (_ev) => {
            onEditTagsClick(candidate);
          },
        },
        {
          dataTestId: `groupprofile-candidate-actions-remove-${candidate.id}`,
          title: "Remove candidate from this group",
          tooltip: accessRemoveCandidateAction ? "Remove candidate from this group" : crmLiteUpgradeMessage,
          icon: <RemoveCircle color="error" sx={{ fontSize: "1rem" }} />,
          disabled: !accessRemoveCandidateAction,
          onClick: () =>
            accessRemoveCandidateAction ? handleSelectCandidateToRemove(candidate.id) : undefined,
          visible: canRemoveCandidates,
        },
      ]}
    />
  );
}
